import { FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Button, TextField, Alert, CircularProgress, Box } from '@mui/material';
import React, { useContext, useState } from 'react';
import { DeliveryState, Workflow } from '../../../enums/orders';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { isMobile, isTablet } from 'react-device-detect';
import { showDeliveryState } from '../styles/order-states.style';
import { useSnackBar } from '../../../contexts/snackbar';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { API_ENDPOINT } from '../../../configurations/global.config';
import axios, { AxiosResponse, isAxiosError } from 'axios';
import { ErrorsFlattenner } from '../errors-flattenner/errors-flattenner';
import FilterChips from '../filter-chips/filter-chips';

interface ChangeStateFormProps {
    orders: OrderType[]
    onSuccess: ()=>void; 
}

enum Action {
    MARK_AS_DELIVERED = "Mark as Delivered",
    MARK_AS_CREATED = "Reset to Created",
    MARK_AT_WAREHOUSE = "Mark At Warehouse",
    MARK_AS_DELIVERED_AT_WAREHOUSE = "Deliver At Warehouse"
}

const actionToDeliveryState = new Map<Action, DeliveryState>([
    [Action.MARK_AS_DELIVERED, DeliveryState.DELIVERED],
    [Action.MARK_AS_CREATED, DeliveryState.CREATED],
    [Action.MARK_AT_WAREHOUSE, DeliveryState.AT_WAREHOUSE],
    [Action.MARK_AS_DELIVERED_AT_WAREHOUSE, DeliveryState.DELIVERED_AT_WAREHOUSE],
])

const getPermittedInitialState = (ac : Action): DeliveryState[] => {
    switch(ac) {
        case Action.MARK_AS_CREATED:
            return [DeliveryState.AT_WAREHOUSE]
        case Action.MARK_AT_WAREHOUSE:
            return [DeliveryState.CREATED, DeliveryState.DELIVERED_AT_WAREHOUSE]
        case Action.MARK_AS_DELIVERED:
            return [DeliveryState.PICKED_UP_FOR_DELIVERY, DeliveryState.IN_TRANSIT]
        case Action.MARK_AS_DELIVERED_AT_WAREHOUSE:
            return [DeliveryState.AT_WAREHOUSE]
    }
}

const getPermittedAction = (deliveryState: DeliveryState): Action[] => {
    switch(deliveryState) {
        case DeliveryState.CREATED:
            return [Action.MARK_AT_WAREHOUSE]
        case DeliveryState.AT_WAREHOUSE:
            return [Action.MARK_AS_CREATED, Action.MARK_AS_DELIVERED_AT_WAREHOUSE]
        case DeliveryState.DELIVERED_AT_WAREHOUSE:
            return [Action.MARK_AT_WAREHOUSE]
        case DeliveryState.PICKED_UP_FOR_DELIVERY:
            return [Action.MARK_AS_DELIVERED]
        case DeliveryState.IN_TRANSIT:
            return [Action.MARK_AS_DELIVERED]
        default:
            return []
}
}


const getPossiblePermittedActions = (deliveryStates: Set<DeliveryState>): Action[] => {
    if (deliveryStates.size == 0) return []

    const res = new Set<Action>();
    deliveryStates.forEach(state => {
        getPermittedAction(state).forEach(action => res.add(action));
    });
    return Array.from(res);
}

function BulkChangeStateForm({ orders, onSuccess }: ChangeStateFormProps) {
    const [value, setValue] = useState<Action>();
    const {showMessage} = useSnackBar()
    const {config} = useContext(AuthContext)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [response, setResponse] = useState<AxiosResponse>()
    const applicableOrders = value ?  orders.filter(order =>  getPermittedAction(order.delivery_state).includes(value)) : []
    const nonApplicableOrders = value ? orders.filter(order =>  !getPermittedAction(order.delivery_state).includes(value)) : []
    const possibleActions = getPossiblePermittedActions(new Set(orders.map(order => order.delivery_state)))
    
    const handleSave = async () => {

        if (applicableOrders.length == 0) {
            showMessage("Operation not allowed for the selected orders.", 'error')
            return
        }

        if (!value) {
            showMessage("No operation selected.", 'error')
            return
        }

        const endpoint = `${API_ENDPOINT}/api/org/orders/bulk-update/`
        setIsLoading(true)
        setResponse(undefined)
        const updatedDeliveryState = actionToDeliveryState.get(value)
        const data = applicableOrders.map(order => { return {
            id: order.id,
            delivery_state: updatedDeliveryState,
        }})
        try {
            await axios.patch(endpoint, data, config)
            showMessage(`Successfully updated ${applicableOrders.length} orders`)
        } catch(e) {   
            showMessage("Failed to update orders", 'error');
            if (isAxiosError(e)) {
                setResponse(e.response)
            }
        } finally {
            setIsLoading(false)
        }
    }
    
     
    if (possibleActions.length == 0) return <Alert severity='warning'>No action is applicable for any of the selected orders.</Alert>
            
    return (<div>
            {
                isLoading && <Box display={"flex"} justifyContent={"center"}><CircularProgress /></Box>
            }

            {
                response && <Alert
                              severity={
                                response && response.status >= 200 && response.status < 300
                                  ? 'success'
                                  : 'error'
                              }
                              sx={{mb :2}}
                            >
                              <>
                                Something went wrong:
                
                                {response && response.status >= 300 && (
                                  <ErrorsFlattenner rawErrors={response.data} />
                                )}
                              </>
                            </Alert>      
            }
            {
                value != undefined && <>
                    {
                        applicableOrders.length == 0 ? <Alert sx={{ mb :2}} severity="error">The selected action is not applicable to the selected orders because of there delivery state.</Alert>
                        : <> {nonApplicableOrders.length > 0 && <Alert sx={{ mb :2}} severity="warning">The selected action is not applicable to the following orders because of there delivery state: <br /><br />
                        
                            {
                                nonApplicableOrders.map(order => order.order_id).join(", ")
                            }
                         </Alert>} </>
                    }                
                </>

            }


            <FormControl sx={{textAlign: "center"}} fullWidth>
                <FormLabel id="demo-radio-buttons-group-label" sx={{mb:1}}>Choose New Order State</FormLabel>
                <Box display={"flex"} justifyContent={"center"} width={"100%"} >
                    {
                        <FilterChips 
                            label={''} 
                            options={possibleActions} selected={value ? [value] : []} onChange={(selected) => {
                                if (selected.length == 1) setValue(selected[0] as Action)
                                else setValue(undefined)
                            }} />
                    }

                </Box >
                
            </FormControl>
            <br /> <br />
            {
                <div style={{ width: "100%", textAlign: "center" }}>

                    <Button  size={(isMobile && !isTablet) ? "small" : "medium"} disabled={applicableOrders.length === 0 || isLoading} onClick={async () => {await handleSave(); onSuccess()}} variant='contained'>Save</Button>
                </div> 
            }

        </div>
    );
}

export default BulkChangeStateForm;